import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  API_RESOURCE_URLS,
  DATE_FORMAT,
  MESSAGE_TYPE,
  RESOURCE_TYPE,
  USER_OPERATIONS,
  AGGREGATE_STATUS,
  AGSN_SELECTOR_HEADER_TEXT,
  cabLinesForAllPlants,
} from '../../constants';
import { useHistory } from 'react-router-dom';
import styles from './AggregateLineOut.module.css';
import { BackButton, DropToolBar } from '..';
import { Popover } from '@material-ui/core';
import ForwardIcon from '@material-ui/icons/Forward';
import { statusStringMap } from '../../staticData';
import { getRelativeDateString } from '../../utils/tableUtils';
import { buildErrorMessage } from '../../apis/calls';
import Table from '../Table/Table';
import AggregateStatusPopup from '../AggregateStatusPopup/AggregateStatusPopup';
import { withAllowedOperationsProvider } from '../../hocs';
import AsnSelectorDisplay from '../AsnSelectorDisplay/AsnSelectorDisplay';
import { getDatesForRange, downloadCsv, getAggregateAuthOperation } from '../../utils/utils';
import { API } from '../../apis/api';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';

const LineoutDataDisplay = ({ rowData }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const ref = useRef(null);

  const openPopup = () => setIsPopupOpen(true);
  const closePopup = () => setIsPopupOpen(false);

  return (
    <>
      <div ref={ref} onClick={openPopup}>
        {statusStringMap[AGGREGATE_STATUS.LINE_OUT]}
      </div>
      <Popover
        open={isPopupOpen}
        anchorEl={ref.current}
        onClose={closePopup}
        onMouseLeave={closePopup}
        anchorOrigin={{
          vertical: 'right',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'left',
          horizontal: 'left',
        }}
      >
        <AggregateStatusPopup agsn={rowData} />
      </Popover>
    </>
  );
};
const AggregateLineOut = ({
  aggregate,
  hasActiveEdits,
  setHasActiveEdits,
  setLineOutCount,
  lastRefreshedDate,
  rowsData,
  setHasLineOutActiveEdit,
  loadLinedOutAgsnOfSelectedPlantAndLineForDate,
  loadAgsnsForADate,
  lastAgsnGenerationDate,
  selectedDate,
  setSelectedDate,
  agsnDates,
}) => {
  const ROW_HEIGHT = 38;
  const history = useHistory();
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [agsnToLineOn, setAgsnToLineOn] = useState(null);
  const [activeDate, setActiveDate] = useState(null);
  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;
  const [aggregateLine] = usePlantAndLinesStore().aggregateLineState;
  const openSelectorPopup = () => setIsSelectorOpen(true);
  const {
    showPopup,
    showInternalError,
    showFailure,
    showLoading,
    closePopUp,
  } = usePopupManager();
  const [isButtonEnable, setIsButtonEnable] = useState(true);
  const closeSelectorPopup = () => setIsSelectorOpen(false);
  const [agsnData, setAgsnData] = useState({
    data: [],
    isLoading: false,
    error: null,
  });

  const baseDefaultColumns = [
    {
      width: 150,
      title: 'AgSN',
      field: 'agsn',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 150,
      title: 'ASN',
      field: 'parentAsn',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 150,
      title: 'Destination',
      field: 'destination',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 170,
      title: 'Part Number',
      field: 'materialNumber',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 235,
      title: 'Part Description',
      field: 'materialDescription',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: 'Shift',
      field: 'aggregateShift',
      enableSearch: true,
      enableFilter: true,
      render: ({ aggregateShift }) => (
        <div style={{ textAlign: 'center' }}>
          {aggregateShift && (
            <div className={styles.shift}>{aggregateShift}</div>
          )}
        </div>
      ),
    },
    cabLinesForAllPlants.includes(aggregateLine.label)
      ? {
        //colour column should be visible to JSR only
        width: 180,
        title: 'Colour',
        field: 'colour',
        enableSearch: true,
        enableFilter: true,
        render: row =>
          row?.attributes?.colourDescription && row?.attributes?.colourCode
            ? `${row?.attributes?.colourDescription} (${row?.attributes?.colourCode})`
            : null,
      }
      : null,
    {
      width: 200,
      title: 'Aggregate Status',
      field: 'aggregateStatus',
      render: row =>
        row.edited ? 'Pending' : <LineoutDataDisplay rowData={row} />,
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 160,
      title: 'Planned Date',
      field: 'originalProductionDate',
      render: row => moment(row.originalProductionDate).format('DD MMMM YYYY'),
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 130,
      title: 'Ageing',
      field: 'ageing',
      render: row =>
        getRelativeDateString(moment(row.originalProductionDate, 'YYYY-MM-DD')),
    },
    {
      width: 150,
      title: 'Lined-Out on',
      field: 'linedOutAt',
      render: row => moment(row.linedOutAt).format('DD MMMM YYYY'),
    },
  ].filter(c => c);

  const fixedColumns = [
    ...baseDefaultColumns.filter(column => {
      const yesterday = moment().subtract(1, 'days');
      if (!yesterday.isSame(selectedDate, 'day'))
        return (
          column.field !== 'originalProductionDate' && column.field !== 'ageing'
        );
      return column.field !== 'linedOutAt';
    }),
  ];

  const fixedAgsnSelectorColumns = [
    {
      width: 50,
      title: '',
      field: null,
      render: row => {
        if (row?.agsnId === agsnToLineOn?.agsnId)
          return <ForwardIcon className={styles.Forwerd} />;
        return null;
      },
    },
    {
      width: 120,
      title: 'AgSN',
      field: 'agsn',
      style: {
        textAlign: 'left',
      },
      render: row => (row?.agsnId ? row.agsn : row.asn),
    },
    {
      width: 120,
      title: 'ASN',
      field: 'parentAsn',
      style: {
        textAlign: 'left',
      },
    },

    {
      width: 120,
      title: 'Destination',
      field: 'destination',
      style: {
        textAlign: 'left',
      },
    },
    {
      width: 120,
      title: 'Aggregate Status',
      field: 'status',
      style: {
        textAlign: 'left',
      },
      render: row => {
        if (row.status === AGGREGATE_STATUS.ASN_GENERATED) return 'PLANNED';
        if (row.status === AGGREGATE_STATUS.LINED_OUT) return ' LINED_OUT';
        if (row.edited) return 'Pending';
        return statusStringMap[row.status];
      },
    },
  ];

  const optionalColumns = [
    {
      width: 200,
      title: 'Vc Alias',
      field: 'rootMaterialAlias',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 170,
      title: 'VC Material',
      field: 'rootMaterial',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 300,
      title: 'VC Material Description',
      field: 'rootMaterialDescription',
      enableSearch: true,
      enableFilter: true,
    }
  ];

  useEffect(() => {
    rowsData.data.map(item => {  
    if (
        item.destination !== plant.label &&
        item.destination !== 'CKD' &&
        item.destination !== 'spare'
      ) {
        item.agsn = item.agsn + '-I';
      }
    });
  }, [plant.label, rowsData]);

  const setCount = count => {
    setLineOutCount(count);
  };

  const redirectToApplicationLandingPage = () => {
    history.push('/');
  };

  const handleDropSelection = row => {
    setAgsnToLineOn(row[0]);
    openSelectorPopup();
  };

  const loadAgsnsForADateForDrop = async (plantId, lineId, date) => {
    if (!plantId) return;
    if (!lineId) return;
    if (!date) return;
    const formattedDate = date.format(DATE_FORMAT.ISO);
    try {
      showLoading();
      setAgsnData(prevState => ({ ...prevState, loading: true }));
      const response = await API.get(API_RESOURCE_URLS.agsn(plantId, lineId), {
        params: {
          date: formattedDate,
          status: [
            AGGREGATE_STATUS.ASN_GENERATED,
            AGGREGATE_STATUS.READY_FOR_DROP,
            AGGREGATE_STATUS.DROPPED,
            AGGREGATE_STATUS.BOOKED,
            AGGREGATE_STATUS.DISPATCHED,
          ],
        },
      });
      const { data: agsnLineup } = response;
      agsnLineup.sort((a, b) => a.sequence - b.sequence);
      const formattedAgsnLineup = agsnLineup.map(item => ({
        ...item,
        materialGroupClusters: [],
      }));
      closePopUp();
      return setAgsnData({
        loading: false,
        data: formattedAgsnLineup,
      });
    } catch (error) {
      if (error?.response?.status === 400) {
        showFailure({});
      } else {
        showInternalError(error);
        console.error(buildErrorMessage(error));
      }
      setAgsnData(prevState => ({ ...prevState, loading: false }));
    }
  };

  const handleLineOn = async agsnData => {
    try {
      setIsButtonEnable(false);
      showLoading();
      const selectedPositionIndex = agsnData.data.findIndex(
        agsn => agsn.isToBeLinedOn,
      );
      const agsnBeforeSelectedAgsn = agsnData.data[selectedPositionIndex - 1];
      await API.patch(API_RESOURCE_URLS.AGSN_LINEON, {
        date: activeDate.format(DATE_FORMAT.ISO),
        position: agsnBeforeSelectedAgsn?.sequence + 1 || 1,
        asnId: agsnToLineOn.agsnId,
      });
      loadLinedOutAgsnOfSelectedPlantAndLineForDate(
        plant.value,
        aggregateLine.value,
        selectedDate,
      );
      loadAgsnsForADate(plant.value, aggregateLine.value, activeDate);
      closePopUp();
      setIsButtonEnable(true);
      showPopup({
        type: MESSAGE_TYPE.SUCCESS,
        contextText: `AgSN ${agsnToLineOn.agsn} lined-on.`,
        info: '',
      });
    } catch (err) {
      console.error(buildErrorMessage(err));
      if (activeDate)
        showPopup({
          error: err,
          contextText: `AGSN line-on failed.`,
        });
      else
        showPopup({
          error: new Error('Please generate AGSN for next day to line on'),
          contextText: `AGSN line-on failed.`,
        });
    }
  };

  const prepareJsonHeaderAndValueFormats = aggregatePartNames => {
    const vcAggregateHeaderAndValueFormats = aggregatePartNames.map(
      partName => ({
        label: partName + ' Status',
        value: row =>
          row.materialGroupClusters.filter(group => group.name === partName)[0]
            ?.materials[0]?.aggregateStatus || 'unavailable',
      }),
    );

    const vcHeaderAndValueFormats = [
      {
        label: 'AGSN',
        value: row => row.agsn,
      },
      {
        label: 'ASN',
        value: row => row.parentAsn,
      },
      {
        label: 'Destination',
        value: row => row.destination,
      },
      {
        label: 'Part Number',
        value: row => '="' + row.materialNumber + '"',
      },
      {
        label: 'VC Alias',
        value: row => row.rootMaterialAlias || '',
      },
      {
        label: 'Part Description',
        value: row => row.materialDescription,
      },
      {
        label: 'Shift',
        value: row => row.aggregateShift,
      },

      {
        label: 'Aggregate Status',
        value: row => row.aggregateStatus,
      },
      {
        label: 'Planned Date',
        value: row => row.productionDate,
      },
      {
        label: 'Lined Out On',
        value: row => moment(row.linedOutAt).format('DD MMMM YYYY'),
      },
      {
        label: 'Ageing',
        value: row =>
          getRelativeDateString(
            moment(row.originalProductionDate, 'YYYY-MM-DD'),
          ),
      },
      {
        label: 'Reason',
        value: row => row.reason,
      },
      {
        label: 'Agency',
        value: row => row.agency,
      },
    ].filter(c => c);

    return [...vcHeaderAndValueFormats, ...vcAggregateHeaderAndValueFormats];
  };

  const generateAndDownloadLineoutCsvReport = filteredData => {
    try {
      if (!rowsData.loading) {
        const dateValue = selectedDate.isBefore(
          moment().format(DATE_FORMAT.ISO),
        )
          ? 'AllDates'
          : selectedDate.format('YYYYMMDD');

        const jsonData = filteredData;

        const aggregatePartNames = [];

        const { parseAsync } = require('json2csv');
        const fields = prepareJsonHeaderAndValueFormats(aggregatePartNames);
        const opts = { fields, quote: '' };
        parseAsync(jsonData, opts)
          .then(csv => {
            const exportedFilename =
              'AgsnLineOutReport_' +
              plant.label +
              '_' +
              line.label +
              '_' +
              dateValue +
              '.csv';
            downloadCsv(csv, exportedFilename);
          })
          .catch(error =>
            console.error(
              'Failed to download Agsn report, error:' +
              buildErrorMessage(error),
            ),
          );
      }
    } catch (error) {
      console.error(
        'Failed to download lineout report, error:' + buildErrorMessage(error),
      );
    }
  };

  useEffect(() => {
    setHasLineOutActiveEdit(hasActiveEdits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveEdits]);

  useEffect(() => {
    const dates = getDatesForRange(moment(), lastAgsnGenerationDate);
    setActiveDate(dates[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastAgsnGenerationDate]);

  useEffect(() => {
    if (isSelectorOpen)
      loadAgsnsForADateForDrop(plant.value, aggregateLine.value, activeDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelectorOpen, activeDate, isButtonEnable]);

  return (
    <>
      <div className={styles.backButton}>
        <BackButton
          basePath={aggregate}
          action="Line Up"
          handleClick={redirectToApplicationLandingPage}
        />
      </div>
      <DropToolBar
        activeDate={selectedDate}
        dates={agsnDates}
        handleDateSelect={date => setSelectedDate(date)}
        lastRefreshedDate={lastRefreshedDate}
        contextualButtonProps={{
          text: 'ALL',
          label: 'all',
          handleClick: () => setSelectedDate(moment().subtract(1, 'days')),
        }}
        restrictNavigationDuringEditing
        hasActiveEdits={hasActiveEdits}
        showAggregateLines
      />
      <div className={styles.container}>
        <div className={styles.tableView} style={{ height: '100%' }}>
          <Table
            columns={[...fixedColumns, ...optionalColumns]}
            defaultColumns={[...fixedColumns]}
            rowHeight={ROW_HEIGHT}
            rows={rowsData.data}
            setHasActiveEdits={setHasActiveEdits}
            setAsnCount={setCount}
            isDataLoading={rowsData.loading}
            displayHash
            primaryAction={{
              name: 'LINE ON',
              authOperation: getAggregateAuthOperation(USER_OPERATIONS.AGGREGATE_LINE_ON, plant),
              shouldEnable: selectedRows => {
                if (selectedRows.length === 1) return true;
              },
              actionFn: filteredData => {
                handleDropSelection(filteredData);
              },
            }}
            secondaryActions={[
              {
                name: 'DOWNLOAD CSV',
                getFilteredData: true,
                authOperation: getAggregateAuthOperation(USER_OPERATIONS.GET_CHASSIS_PLAN_ASN, plant),
                shouldEnable: () => rowsData.data.length > 0,
                actionFn: filteredData =>
                  generateAndDownloadLineoutCsvReport(filteredData),
              },
            ]}
          />
        </div>
        <AsnSelectorDisplay
          open={isSelectorOpen}
          handleClose={closeSelectorPopup}
          asnData={agsnData}
          setAsnData={setAgsnData}
          updateRows={loadLinedOutAgsnOfSelectedPlantAndLineForDate}
          handleLineOnSubmit={handleLineOn}
          asnToLineOn={agsnToLineOn}
          lastAsnOrAgsnGeneratedDate={lastAgsnGenerationDate}
          activeDate={activeDate}
          setActiveDate={setActiveDate}
          fixedColumns={fixedAgsnSelectorColumns}
          asnSelectorheaderText={AGSN_SELECTOR_HEADER_TEXT}
          selectedLine={aggregateLine.label}
        />
      </div>
    </>
  );
};

AggregateLineOut.propTypes = {
  hasActiveEdits: PropTypes.bool.isRequired,
  setHasActiveEdits: PropTypes.func.isRequired,
  aggregate: PropTypes.string.isRequired,
  lastRefreshedDate: PropTypes.objectOf(moment),
  rowsData: PropTypes.arrayOf(
    PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      data: PropTypes.array.isRequired,
      filteredData: PropTypes.array.isRequired,
    }),
  ),
  setHasLineOutActiveEdit: PropTypes.func.isRequired,
  setLineOutCount: PropTypes.func.isRequired,
  lastAgsnGenerationDate: PropTypes.objectOf(moment),
  loadLinedOutAgsnOfSelectedPlantAndLineForDate: PropTypes.func.isRequired,
  loadAgsnsForADate: PropTypes.func.isRequired,
  selectedDate: PropTypes.func.isRequired,
  setSelectedDate: PropTypes.func.isRequired,
  agsnDates: PropTypes.array.isRequired,
};

export default withAllowedOperationsProvider(
  AggregateLineOut,
  true,
  RESOURCE_TYPE.MATERIAL,
);
